/**
 * Dashboard Hooks
 *
 * React Query hooks for dashboard data
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { normalizeArray, normalizeObject } from '@/lib/api-normalizer';

/**
 * Types
 */

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
}

export interface WorkspaceWithStats extends Workspace {
  memberCount: number;
  userRole: string;
}

export interface Repository {
  id: string;
  name: string;
  description: string | null;
  source: string;
  sourceUrl: string | null;
  sizeBytes: number | null;
  fileCount: number | null;
  lastAnalyzedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UsageStat {
  eventType: string;
  count: number;
}

export interface UsageLimit {
  eventType: string;
  count: number;
  limit: number;
  remaining: number;
  period: string;
}

export interface DashboardStats {
  totalRepositories: number;
  totalPRDs: number;
  totalAISessions: number;
  totalAnalyses: number;
  recentActivity: UsageStat[];
}

/**
 * API Response Types
 */

interface WorkspacesResponse {
  workspaces: Workspace[];
}

interface WorkspaceResponse {
  workspace: WorkspaceWithStats;
}

interface RepositoriesResponse {
  repositories: Repository[];
}

interface UsageStatsResponse {
  workspaceId: string;
  period: string;
  stats: UsageStat[];
}

interface UsageLimitsResponse {
  workspaceId: string;
  planId: string;
  period: string;
  limits: UsageLimit[];
}

/**
 * Hook: useWorkspaces
 *
 * Fetch all workspaces for the current user
 */
export function useWorkspaces() {
  return useQuery<Workspace[]>({
    queryKey: ['workspaces'],
    queryFn: async () => {
      try {
        const response = await api.get<WorkspacesResponse>('/workspaces');
        return normalizeArray<Workspace>(response, 'workspaces');
      } catch (error) {
        console.error('Failed to fetch workspaces:', error);
        throw error;
      }
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook: useWorkspace
 *
 * Fetch a single workspace with stats
 */
export function useWorkspace(workspaceId: string | null) {
  return useQuery<WorkspaceWithStats | null>({
    queryKey: ['workspace', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return null;
      const response = await api.get<WorkspaceResponse>(`/workspaces/${workspaceId}`);
      return normalizeObject<WorkspaceWithStats>(response, 'workspace');
    },
    enabled: !!workspaceId,
  });
}

/**
 * Hook: useRepositories
 *
 * Fetch repositories for a workspace
 */
export function useRepositories(workspaceId: string | null) {
  return useQuery<Repository[]>({
    queryKey: ['repositories', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      try {
        const response = await api.get<RepositoriesResponse>(
          `/workspaces/${workspaceId}/repositories`
        );
        return normalizeArray<Repository>(response, 'repositories');
      } catch (error) {
        console.error('Failed to fetch repositories:', error);
        throw error;
      }
    },
    enabled: !!workspaceId,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook: useUsageStats
 *
 * Fetch usage statistics for a workspace
 */
export function useUsageStats(workspaceId: string | null, period: 'day' | 'month' | 'total' = 'month') {
  return useQuery<UsageStat[]>({
    queryKey: ['usage-stats', workspaceId, period],
    queryFn: async () => {
      if (!workspaceId) return [];
      try {
        const response = await api.get<UsageStatsResponse>(
          `/usage/stats?workspaceId=${workspaceId}&period=${period}`
        );
        return normalizeArray<UsageStat>(response, 'stats');
      } catch (error) {
        console.error('Failed to fetch usage stats:', error);
        throw error;
      }
    },
    enabled: !!workspaceId,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook: useUsageLimits
 *
 * Fetch usage limits for a workspace
 */
export function useUsageLimits(workspaceId: string | null) {
  return useQuery<UsageLimit[]>({
    queryKey: ['usage-limits', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      try {
        const response = await api.get<UsageLimitsResponse>(
          `/usage/limits?workspaceId=${workspaceId}`
        );
        return normalizeArray<UsageLimit>(response, 'limits');
      } catch (error) {
        console.error('Failed to fetch usage limits:', error);
        throw error;
      }
    },
    enabled: !!workspaceId,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook: useDashboardStats
 *
 * Aggregates data for the dashboard overview
 */
export function useDashboardStats(workspaceId: string | null) {
  const { data: repositories = [], isLoading: reposLoading } = useRepositories(workspaceId);
  const { data: usageStats = [], isLoading: statsLoading } = useUsageStats(workspaceId, 'month');

  const stats: DashboardStats = {
    totalRepositories: repositories.length,
    totalPRDs: usageStats.find((s) => s.eventType === 'prd_generated')?.count ?? 0,
    totalAISessions: usageStats.find((s) => s.eventType === 'acf_session_created')?.count ?? 0,
    totalAnalyses: usageStats.find((s) => s.eventType === 'analysis_completed')?.count ?? 0,
    recentActivity: usageStats,
  };

  return {
    data: stats,
    isLoading: reposLoading || statsLoading,
  };
}
